/**
 * GRI Disclosure Panel Component
 *
 * Renders all questions for a specific disclosure within a standard.
 * Handles loading questions and managing responses with auto-save.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Skeleton,
} from '@mui/material';
import { ExpandMore, CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import {
  griService,
  GRIDisclosure,
  GRIQuestion,
  GRIResponseValue,
} from '../../services/griService';
import { GRIQuestionInput } from './GRIQuestionInput';

interface GRIDisclosurePanelProps {
  standardId: string;
  sessionId: string;
  isEditable: boolean;
  onResponseSaved?: () => void;
}

export const GRIDisclosurePanel: React.FC<GRIDisclosurePanelProps> = ({
  standardId,
  sessionId,
  isEditable,
  onResponseSaved,
}) => {
  const [disclosures, setDisclosures] = useState<GRIDisclosure[]>([]);
  const [questionsMap, setQuestionsMap] = useState<Record<string, GRIQuestion[]>>({});
  const [loadingDisclosures, setLoadingDisclosures] = useState(true);
  const [loadingQuestions, setLoadingQuestions] = useState<Record<string, boolean>>({});
  const [expandedDisclosure, setExpandedDisclosure] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingQuestions, setSavingQuestions] = useState<Set<string>>(new Set());

  // Load disclosures for the standard
  useEffect(() => {
    const loadDisclosures = async () => {
      setLoadingDisclosures(true);
      setError(null);
      try {
        const data = await griService.standards.getDisclosures(standardId);
        setDisclosures(data);
        // Auto-expand first disclosure
        if (data.length > 0) {
          setExpandedDisclosure(data[0].id);
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load disclosures';
        setError(errorMessage);
      } finally {
        setLoadingDisclosures(false);
      }
    };

    loadDisclosures();
  }, [standardId]);

  // Load questions when a disclosure is expanded
  useEffect(() => {
    const loadQuestions = async (disclosureId: string) => {
      if (questionsMap[disclosureId]) return; // Already loaded

      setLoadingQuestions((prev) => ({ ...prev, [disclosureId]: true }));
      try {
        const questions = await griService.questions.list(disclosureId, sessionId);
        setQuestionsMap((prev) => ({ ...prev, [disclosureId]: questions }));
      } catch (err: unknown) {
        console.error('Failed to load questions:', err);
      } finally {
        setLoadingQuestions((prev) => ({ ...prev, [disclosureId]: false }));
      }
    };

    if (expandedDisclosure && !questionsMap[expandedDisclosure]) {
      loadQuestions(expandedDisclosure);
    }
  }, [expandedDisclosure, sessionId, questionsMap]);

  // Handle response changes with auto-save
  const handleResponseChange = useCallback(
    async (questionId: string, responseValue: Partial<GRIResponseValue>) => {
      setSavingQuestions((prev) => new Set(prev).add(questionId));

      try {
        await griService.responses.save(sessionId, {
          question_id: questionId,
          text_value: responseValue.text_value,
          number_value: responseValue.number_value,
          boolean_value: responseValue.boolean_value,
          date_value: responseValue.date_value,
          json_value: responseValue.json_value as Record<string, unknown> | undefined,
        });

        // Update local state
        setQuestionsMap((prev) => {
          const updated = { ...prev };
          for (const disclosureId of Object.keys(updated)) {
            updated[disclosureId] = updated[disclosureId].map((q) => {
              if (q.id === questionId) {
                return {
                  ...q,
                  current_response: {
                    ...q.current_response,
                    ...responseValue,
                    updated_at: new Date().toISOString(),
                  },
                };
              }
              return q;
            });
          }
          return updated;
        });

        // Notify parent to refresh progress
        onResponseSaved?.();
      } catch (err: unknown) {
        console.error('Failed to save response:', err);
      } finally {
        setSavingQuestions((prev) => {
          const next = new Set(prev);
          next.delete(questionId);
          return next;
        });
      }
    },
    [sessionId, onResponseSaved]
  );

  // Calculate disclosure completion
  const getDisclosureProgress = (disclosureId: string): { answered: number; total: number } => {
    const questions = questionsMap[disclosureId] || [];
    const answered = questions.filter((q) => {
      const r = q.current_response;
      return r && (r.text_value || r.number_value !== undefined || r.boolean_value !== undefined || r.date_value || r.json_value);
    }).length;
    return { answered, total: questions.length };
  };

  if (loadingDisclosures) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={60} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {disclosures.map((disclosure) => {
        const progress = getDisclosureProgress(disclosure.id);
        const isComplete = progress.total > 0 && progress.answered === progress.total;

        return (
          <Accordion
            key={disclosure.id}
            expanded={expandedDisclosure === disclosure.id}
            onChange={() =>
              setExpandedDisclosure(
                expandedDisclosure === disclosure.id ? null : disclosure.id
              )
            }
            sx={{
              border: '1px solid #e5e7eb',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              mb: 1,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  width: '100%',
                  pr: 2,
                }}
              >
                {isComplete ? (
                  <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                ) : (
                  <RadioButtonUnchecked sx={{ color: '#9ca3af', fontSize: 20 }} />
                )}
                <Chip
                  label={disclosure.code}
                  size="small"
                  sx={{
                    backgroundColor: isComplete ? '#dcfce7' : '#f3f4f6',
                    color: isComplete ? '#166534' : '#374151',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                    {disclosure.title}
                  </Typography>
                </Box>
                {questionsMap[disclosure.id] && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#6b7280',
                      backgroundColor: '#f9fafb',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    {progress.answered}/{progress.total}
                  </Typography>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: '#f9fafb' }}>
              {disclosure.guidance && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">{disclosure.guidance}</Typography>
                </Alert>
              )}

              {loadingQuestions[disclosure.id] ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {questionsMap[disclosure.id]?.map((question, idx) => (
                    <Box key={question.id}>
                      {idx > 0 && <Divider sx={{ mb: 2 }} />}
                      <Box sx={{ position: 'relative' }}>
                        <GRIQuestionInput
                          question={question}
                          value={question.current_response}
                          onChange={handleResponseChange}
                          disabled={!isEditable}
                        />
                        {savingQuestions.has(question.id) && (
                          <CircularProgress
                            size={16}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default GRIDisclosurePanel;
